import { MehOutlined, SmileOutlined, UserSwitchOutlined } from '@ant-design/icons';
import { Result } from 'antd';
import { get, isString } from 'lodash-es';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';

import { WrapperButton, WrapperDescription, WrapperInvite } from '@page/invite/invite.style';
import { authActions } from '@store/auth/auth.slice';
import { commonActions } from '@store/common/common.slice';
import { UnsafePageDescription, UnsafePageTitle, UnsafePageWrapper } from '@style/pages/page.style';

import { Button } from '@atom/button/button';
import { ButtonTypeEnum } from '@atom/button/button.enum';
import { Text, Title } from '@atom/typography/typography';
import { LoaderPage } from '@component/loader/loader.page';
import { RouteEnum } from '@enum/route.enum';
import { ColorEnum } from '@enum/style/color.enum';
import { useAcceptInviteMutation, useGetInviteInfoQuery } from '@hook/memeber/memeber.hooks';
import { useStoreDispatch } from '@hook/store/store.hooks';
import { useLocalStorage } from '@hook/store/use-local-storage.hook';

export const InvitePage = () => {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const [, saveInvite] = useLocalStorage<string>('invite');
  const dispatch = useStoreDispatch();

  const [action, { isSuccess, isLoading, error }] = useAcceptInviteMutation();
  const { data: infoData, isLoading: isInfoLoading } = useGetInviteInfoQuery(code, {
    skip: !isString(code),
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
    refetchOnFocus: true,
  });

  const teamName = get(infoData, ['data', 'name']);
  const isMember = get(infoData, ['data', 'isMember']);
  const hasInvite = isString(teamName);
  const errorMessage = get(error, ['data', 'error', 'message'], '');

  const handleInvite = () => action(code);
  const handleRejectInvite = (path?: string) => () => {
    saveInvite(undefined);
    dispatch(commonActions.setInvite(''));
    if (isString(path)) navigate(path);
  };
  const handleLogout = (path: string) => () => {
    dispatch(authActions.logout());
    navigate(path);
  };

  useEffect(() => void (!isString(code) ? navigate(RouteEnum.Dashboard.path) : saveInvite(code)), [code]);
  useEffect(() => void (isSuccess && handleRejectInvite(RouteEnum.Dashboard.path)()), [isSuccess]);

  if (isInfoLoading) return <LoaderPage />;

  return (
    <UnsafePageWrapper>
      <UnsafePageTitle>Accept invitation</UnsafePageTitle>
      <UnsafePageDescription>Make your choice</UnsafePageDescription>
      <Result
        icon={!hasInvite ? <MehOutlined /> : !isMember ? <UserSwitchOutlined /> : <SmileOutlined />}
        status={!hasInvite ? 'error' : !isMember ? 'warning' : 'info'}
        title={teamName}
        extra={
          hasInvite && (
            <>
              <WrapperButton>
                {isMember && (
                  <WrapperInvite>
                    <Button loading={isLoading} type={ButtonTypeEnum.Primary} block onClick={handleInvite}>
                      Accept invite
                    </Button>
                    <Text color={ColorEnum.Error100} level='m' paragraph>
                      {errorMessage}
                    </Text>
                  </WrapperInvite>
                )}
                {hasInvite && !isMember && (
                  <>
                    <Button type={ButtonTypeEnum.Primary} onClick={handleLogout(RouteEnum.Login.path)}>
                      Login before accept invite
                    </Button>
                    <Button type={ButtonTypeEnum.Default} onClick={handleLogout(RouteEnum.SignUp.path)}>
                      Sign up before accept invite
                    </Button>
                  </>
                )}
              </WrapperButton>
              <WrapperDescription>
                <Button
                  href={RouteEnum.Dashboard.path}
                  type={ButtonTypeEnum.Link}
                  onClick={handleRejectInvite(RouteEnum.Dashboard.path)}
                >
                  Skip and go to Dashboard
                </Button>
              </WrapperDescription>
            </>
          )
        }
        subTitle={
          hasInvite && !isMember
            ? 'You try to join team by other user. Please change account'
            : hasInvite && 'You have been invited to join the team'
        }
      >
        {!hasInvite && (
          <WrapperDescription>
            <Title level={4} mb={16}>
              Oh, no!
            </Title>
            <Text level='m' paragraph>
              Your invitation has expired or not found. Please contact your administrator.
            </Text>
            <Button href={RouteEnum.Dashboard.path} type={ButtonTypeEnum.Link} onClick={handleRejectInvite()}>
              Go to Dashboard
            </Button>
          </WrapperDescription>
        )}
      </Result>
    </UnsafePageWrapper>
  );
};
